import React, { useEffect, useState } from 'react';

export default function Lightbox({ images, currentIndex, onClose, onPrev, onNext }) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance in pixels
  const minSwipeDistance = 50;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };

    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onPrev, onNext]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      onNext();
    } else if (isRightSwipe) {
      onPrev();
    }
  };

  if (currentIndex === null || !images || images.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-950/95 backdrop-blur-sm"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Top Bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between p-6 text-white z-10">
        <span className="text-sm font-semibold font-display tracking-wider">
          Billede {currentIndex + 1} af {images.length}
        </span>
        <button
          onClick={onClose}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-all"
          aria-label="Luk visning"
        >
          <i className="fa-regular fa-xmark text-xl" />
        </button>
      </div>

      {/* Main Image Container */}
      <div className="relative flex h-full w-full items-center justify-center px-4 md:px-16">
        {/* Left Arrow (Hidden on mobile if swiping is preferred, but shown on desktop) */}
        <button
          onClick={onPrev}
          className="absolute left-6 z-10 hidden h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-all md:flex"
          aria-label="Forrige billede"
        >
          <i className="fa-regular fa-chevron-left text-xl" />
        </button>

        {/* Image */}
        <div className="relative max-h-[75vh] max-w-[90vw] overflow-hidden rounded-2xl shadow-2xl">
          <img
            src={images[currentIndex]}
            alt={`Pabst Teknik reference- og galleribillede ${currentIndex + 1} af ${images.length}`}
            className="max-h-[75vh] max-w-[90vw] object-contain select-none"
            draggable={false}
          />
        </div>

        {/* Right Arrow */}
        <button
          onClick={onNext}
          className="absolute right-6 z-10 hidden h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-all md:flex"
          aria-label="Næste billede"
        >
          <i className="fa-regular fa-chevron-right text-xl" />
        </button>
      </div>

      {/* Bottom Swipe Indicator for Mobile */}
      <div className="absolute bottom-6 text-center text-xs text-slate-400 md:hidden">
        <i className="fa-regular fa-hand-pointer animate-pulse mr-2" />
        Svejs til siden for at skifte billede
      </div>
    </div>
  );
}
